import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import SidePanelController from '../controllers/SidePanelController';
import classNames from 'classnames';
import styles from './styles.module.css';
import { capitalize, result } from 'lodash';
import { type WhiteboardSidePanelItem } from '../../../sidepanel-types';
import { type PromoLinkValue } from '../../../shared-types';

const SidePanel: React.FC<{
  active: boolean;
  item: WhiteboardSidePanelItem;
}> = ({ active, item }) => {
  const render = active || item.alwaysRendered;

  const {
    content,
    titleContent,
    panel,
    contentClassName,
    headerContent,
    footerContent,
  } = item;

  if (!render) {
    return null;
  }

  return (
    <div
      key={item.name}
      className={classNames(styles.sidePanelSlideItemInner, {
        [styles.sidePanelSlideItemInnerActive]: active,
      })}
    >
      {panel ?? (
        <>
          <div className={styles.sidePanelHeader}>
            <div className={styles.sidePanelTitle}>
              <h3
                className={classNames('h-500', styles.sidePanelTitleText)}
                style={{ marginBottom: 0 }}
              >
                {item.name}
              </h3>
              {titleContent && (
                <div className={styles.sidePanelTitleComponent}>
                  {titleContent}
                </div>
              )}
            </div>
            {headerContent}
          </div>
          <div
            className={`${styles.sidePanelContent} sidepanel-scroll-parent ${
              contentClassName ?? ''
            }`.trim()}
          >
            {content}
          </div>
          {footerContent && (
            <div className={styles.sidePanelFooter}>{footerContent}</div>
          )}
        </>
      )}
    </div>
  );
};

const SidePanelSlider: React.FC = () => {
  const controller = useController(SidePanelController);
  const { sidePanelItems } = controller.props;
  const {
    sidePanelStack,
    slideDepth,
    activeMenuItemIndex,
    disableSlideAnimation,
    sidePanelOpen,
  } = controller.state;

  const content = sidePanelItems.map((item: WhiteboardSidePanelItem, index) => (
    <SidePanel
      active={index === activeMenuItemIndex && sidePanelOpen}
      item={item}
      key={item.name}
    />
  ));

  return (
    <div
      style={{
        left: `calc(${-(slideDepth - 1)} * var(--side-panel--menu-width))`,
      }}
      className={classNames(styles.sidePanelSlider, {
        [styles.sidePanelSliderDepth1]: slideDepth === 1,
        [styles.sidePanelSliderTransitionDisabled]: disableSlideAnimation,
      })}
    >
      <div
        className={classNames(
          styles.sidePanelSlideItem,
          styles.sidePanelSlideLevel1
        )}
      >
        <ul className={styles.sidePanelItems}>
          {sidePanelItems.map((item: WhiteboardSidePanelItem, index) => {
            let upgradeLocation = '';
            let upgradeLocationParam;
            const promoLink = result<PromoLinkValue>(item, 'promoLink');
            if (promoLink) {
              upgradeLocationParam = promoLink
                .split('?upgrade_location=')
                .pop();
              upgradeLocation = capitalize(upgradeLocationParam)?.replace(
                /_/g,
                ' '
              );
            }

            return (
              <li
                key={item.name}
                className={styles.sidePanelItem}
                data-testid={item.testId ?? null}
              >
                <button
                  className={styles.sidePanelItemButton}
                  type='button'
                  onClick={() => {
                    if (promoLink) {
                      window.open(promoLink, '_self');

                      window.AhaAnalytics?.track(
                        'Feature-7333230858416702915',
                        'User clicks promotion',
                        {
                          'Upgrade location': upgradeLocation,
                          'Promotion product': 'Whiteboards',
                          'Originating product':
                            window.currentAccount?.primaryFlavor,
                        }
                      );
                    } else {
                      controller.actionLoadMenuItem(index);
                    }
                  }}
                >
                  <span className={styles.sidePanelItemIcon}>
                    {typeof item.icon === 'string' ? (
                      <i className={item.icon} />
                    ) : (
                      item.icon
                    )}
                  </span>
                  <span className={styles.sidePanelItemName}>
                    {item.name}
                    {promoLink && (
                      <aha-promo-try-pill
                        font-sizing='large'
                        pill-size='small'
                        upgrade-location={upgradeLocationParam}
                        style={{ marginLeft: 'var(--spacer-2)' }}
                      />
                    )}
                  </span>
                  <span className={styles.sidePanelItemChevron}>
                    <i className='fa-regular fa-chevron-right' />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div
        className={classNames(
          styles.sidePanelSlideItem,
          styles.sidePanelSlideLevel2
        )}
      >
        {content}
      </div>

      {sidePanelStack.map((item: WhiteboardSidePanelItem) => {
        return (
          <div
            key={item.name}
            className={classNames(styles.sidePanelSlideItem)}
          >
            <SidePanel active={sidePanelOpen} item={item} />
          </div>
        );
      })}
    </div>
  );
};

export default ApplicationView(SidePanelSlider);
